import React, { useEffect, useState } from 'react';
import styles from '../CardsAndWallets.module.scss';
import { Typography, Menu, MenuItem, styled } from '@mui/material';
import { Button, Loader, HelpTooltip, openNotification } from 'tt-ui-lib/core';
import { AddIcon, BankCardIcons, StarIcon, LitterBinIcon } from 'tt-ui-lib/icons';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddNewCardModal from './AddNewCardModal';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DELETE_PAYMENT_CARD,
  GET_CUSTOMER_ACCOUNT_PAYMENT_METHODS,
  SET_CARD_AS_DEFAULT,
} from '../../../../api';

const options = {
  defaultCardOptions: [
    {
      title: 'Delete',
      icon: <LitterBinIcon style={{ width: 20, height: 20 }} />,
      actionName: 'deleteItem',
    },
  ],
  cardOptions: [
    {
      title: 'Set as default',
      icon: <StarIcon style={{ width: 20, height: 20 }} />,
      actionName: 'setAsDefault',
    },
    {
      title: 'Delete',
      icon: <LitterBinIcon style={{ width: 20, height: 20 }} />,
      actionName: 'deleteItem',
    },
  ],
};

const Cards = () => {
  const [cardsList, setCardsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const openOptions = Boolean(anchorEl);
  const [openAddNewCardModal, setOpenAddNewCardModal] = useState(false);
  const [deletePaymentCard] = useMutation(DELETE_PAYMENT_CARD);
  const [setAsDefault] = useMutation(SET_CARD_AS_DEFAULT);
  const [getCustomerAccountPaymentMethods] = useLazyQuery(GET_CUSTOMER_ACCOUNT_PAYMENT_METHODS, {
    fetchPolicy: 'network-only',
  });

  const handleClose = () => {
    setAnchorEl(null);
  };

  const deleteCard = async (id) => {
    const res = await deletePaymentCard({ variables: { id } });
    if (res.data && res.data.deletePaymentCard) {
      setCardsList((prevCards) => prevCards.filter((card) => card.id !== id));
      openNotification({
        type: 'success',
        message: `Card deleted successfully`,
      });
    } else {
      openNotification({
        type: 'error',
        message: `Failed to delete card`,
      });
    }
    handleClose();
  };

  const setCardAsDefault = async (id) => {
    const res = await setAsDefault({ variables: { input: { id, payment_system: 'stripe' } } });
    if (res.data && res.data.setCardAsDefault) {
      setCardsList((prevCards) => {
        const updatedCards = prevCards.map((card) =>
          card.id === id ? { ...card, is_default: true } : { ...card, is_default: false }
        );

        const defaultCard = updatedCards.find((card) => card.id === id);
        const filteredCards = updatedCards.filter((card) => card.id !== id);

        return [defaultCard, ...filteredCards];
      });

      openNotification({
        type: 'success',
        message: `Card set as default successfully`,
      });
    } else {
      openNotification({
        type: 'error',
        message: `Failed to set card as default`,
      });
    }
    handleClose();
  };

  const optionActions = {
    deleteItem: (id) => deleteCard(id),
    setAsDefault: (id) => setCardAsDefault(id),
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await getCustomerAccountPaymentMethods({});
      if (res.data && res.data.getCustomerAccountPaymentMethods) {
        setCardsList(res.data.getCustomerAccountPaymentMethods);
      }
      setLoading(false);
    })();
  }, []);

  const StyledMenu = styled((props) => (
    <Menu
      id="pickup-options-menu"
      anchorEl={anchorEl}
      open={openOptions}
      onClose={handleClose}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      boxShadow: '0px 4px 32px rgba(0, 0, 0, 0.08)',
      borderRadius: '12px',
      '& .MuiMenu-list': {},
    },
  }));

  const handleClick = (event, walletId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveItem(walletId);
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.contentBoxWallet}>
        {loading ? (
          <div className={styles.emptyContainer}>
            <Loader />
          </div>
        ) : (
          <>
            <div className={styles.cardsBlockHeader}>
              <div className={styles.title}>
                <Typography className={styles.text}>Saved cards</Typography>
                <HelpTooltip title="Saved cards are cards that you have saved for future payments." />
              </div>
              <Button
                type="mainButton"
                className={styles.addWalletBtnWhite}
                onClick={() => setOpenAddNewCardModal(true)}
              >
                <AddIcon style={{ width: 24, height: 24 }} />
                Add new card
              </Button>
            </div>
            {cardsList.length > 0 ? (
              <div className={styles.cardsList}>
                {cardsList.length > 0 &&
                  cardsList.map((card) => (
                    <div className={styles.cardItem}>
                      <div className={styles.cardNumber}>
                        <BankCardIcons style={{ width: 32, height: 32 }} brand={card.brand} />
                        <Typography className={styles.number}>
                          ***** **** **** {card.last4}
                        </Typography>
                        {card.is_default && (
                          <div className={styles.defaultCard}>
                            <StarIcon style={{ width: 16, height: 16 }} />
                            <Typography className={styles.text}>Default</Typography>
                          </div>
                        )}
                      </div>
                      <div className={styles.dateBlock}>
                        <Typography className={styles.connectDate}>Expiration date:</Typography>
                        <Typography className={styles.connectDate}>
                          {card.exp_month}/{card.exp_year.toString().slice(-2)}
                        </Typography>
                      </div>
                      <div className={styles.optionsIcon} onClick={(e) => handleClick(e, card.id)}>
                        <MoreVertIcon />
                      </div>
                      {activeItem === card.id && (
                        <StyledMenu>
                          {options[card.is_default ? 'defaultCardOptions' : 'cardOptions'].map(
                            (option) => (
                              <MenuItem
                                className={styles.menuItemWrapper}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  optionActions[option.actionName](card.id);
                                }}
                              >
                                {option.icon}
                                <Typography>{option.title}</Typography>
                              </MenuItem>
                            )
                          )}
                        </StyledMenu>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.emptyContainer}>There is no connected cards</div>
            )}
            {setOpenAddNewCardModal && (
              <AddNewCardModal
                open={openAddNewCardModal}
                setOpen={setOpenAddNewCardModal}
                setCardsList={setCardsList}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Cards;
